/**
 * Upload Configuration Constants
 * Centralizes all upload-related configuration for easy management
 */

export const UPLOAD_CONFIG = {
  // file size threshold for multipart upload (10MB)
  // files >= this size will use multipart upload, smaller files use simple upload
  MULTIPART_THRESHOLD: 10 * 1024 * 1024, // 10MB in bytes
  
  // Chunk size for multipart uploads (10MB per chunk)
  // note: AwsS3 plugin manages chunk size internally based on file size
  CHUNK_SIZE: 10 * 1024 * 1024, // 10MB
  
  // S3 minimum chunk size (5MB, except last part)
  // AWS S3 requirement - all parts except the last must be >= 5MB
  MIN_CHUNK_SIZE: 10 * 1024 * 1024, // 10MB
  
  // Maximum chunk size (100MB)
  MAX_CHUNK_SIZE: 100 * 1024 * 1024, // 100MB
  
  // maximum number of concurrent file uploads
  MAX_PARALLEL_FILES: 2,
  
  // Retry delays for failed chunks (milliseconds)
  // exponential backoff: instant, 1s, 3s, 7s, 15s
  RETRY_DELAYS: [1000, 3000, 7000, 15000],
  
  // Maximum retries per chunk before giving up
  MAX_RETRIES: 3,
  
  // Presigned URL validity duration (minutes)
  // Backend should generate URLs valid for at least this long
  PRESIGNED_URL_EXPIRY: 60, // 60 minutes
  
  // Golden Retriever settings (for cross-session (browser refresh) resume)
  RESUME_STORAGE_EXPIRY: 24 * 60 * 60 * 1000, // 24 hours
  ENABLE_CROSS_SESSION_RESUME: false, // not used, not compatible with current implementation

  // Rate limiting
  RATE_LIMIT_STATUS_CODE: 429,
  DEFAULT_RETRY_AFTER_MS: 10000, // 10s
  MAX_RETRY_AFTER_MS: 60000, // 1m

  // Network reconnection
  NETWORK_CHECK_INTERVAL_MS: 3000, // 3s
  ENABLE_AUTO_RETRY_ON_RECONNECT: true,

  // Scroll hint threshold: show a hint to scroll when the number of files uploading is greater than this threshold
  SCROLL_HINT_THRESHOLD: 50,

  // Sample image upload
  SAMPLE_IMAGE_UPLOAD_MAX_FILES: 5,
  SAMPLE_IMAGE_UPLOAD_ALLOWED_FILE_TYPES: ['.png', '.jpg', '.jpeg', '.psd', '.tif', '.tiff', '.arw'],

  // Message attachment CDN upload (order discussion chat attachments)
  // When true, attachments use presigned URL cdn upload flow (generate → PUT to CDN → confirm).
  // When false, attachments are uploaded directly to backend server (legacy).
  MESSAGE_ATTACHMENT_CDN_UPLOAD: true,
};

/**
 * Helper function to determine if file should use multipart upload
 * @param {number} fileSize - File size in bytes
 * @returns {boolean} - True if file should use multipart upload
 */
export function shouldUseMultipart(fileSize) {
  return fileSize >= UPLOAD_CONFIG.MULTIPART_THRESHOLD;
}

/**
 * Helper function to format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted string (e.g., "10.5 MB")
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Helper function to calculate number of parts for a file
 * @param {number} fileSize - File size in bytes
 * @param {number} chunkSize - Chunk size in bytes
 * @returns {number} - Number of parts
 */
export function calculatePartCount(fileSize, chunkSize = UPLOAD_CONFIG.CHUNK_SIZE) {
  return Math.ceil(fileSize / chunkSize);
}
